<!-- 7秒ミニドリルコンポーネント（A: リズム、B: 最小ペア、C: リンク＆ブレイク） -->
<template>
  <div class="quick-drill-modal" @click.self="handleClose">
    <div class="drill-content">
      <div class="drill-header">
        <h3>⚡ 7秒で直す: {{ targetWord.word }}</h3>
        <button @click="handleClose" class="close-button">×</button>
      </div>
      
      <!-- 現在のドリル表示 -->
      <div class="drill-body">
        <!-- A: リズム合わせ -->
        <div v-if="currentDrill === 'rhythm'" class="drill-section rhythm-drill">
          <p class="drill-description">
            参照音声を聞いて、リズムと強勢を合わせて発音しましょう。
          </p>
          
          <div class="reference-audio" v-if="referenceAudioUrl">
            <audio
              ref="referenceAudioRef"
              :src="referenceAudioUrl"
              controls
              @ended="onReferenceAudioEnded"
            ></audio>
          </div>
          
          <div class="stress-indicator" v-if="stressPoints.length > 0">
            <span
              v-for="(point, index) in stressPoints"
              :key="index"
              class="stress-point"
              :class="{ active: point.active }"
            >
              {{ point.syllable }}
            </span>
          </div>
          
          <button
            @click="startRecordingForDrill"
            :disabled="isDrillRecording || isDrillEvaluating"
            class="drill-record-button"
          >
            <span v-if="isDrillRecording">⏹️ 停止</span>
            <span v-else-if="isDrillEvaluating">⏳ 評価中...</span>
            <span v-else>🎤 録音開始</span>
          </button>
          
          <div class="drill-result" v-if="drillResult">
            <p>スコア: {{ drillResult.score }}点</p>
            <p v-if="drillResult.success" class="success-message">✅ 成功！</p>
            <p v-else class="retry-message">もう一度挑戦してみましょう</p>
          </div>
        </div>
        
        <!-- B: 最小ペア -->
        <div v-if="currentDrill === 'minimal_pair'" class="drill-section minimal-pair-drill">
          <p class="drill-description">
            問題音素の最小ペアを練習しましょう。
          </p>
          
          <div class="minimal-pairs-list">
            <div
              v-for="(pair, index) in minimalPairs"
              :key="index"
              class="pair-item"
            >
              <span class="pair-word">{{ pair.word }}</span>
              <span class="pair-phoneme">/{{ pair.phoneme }}/</span>
            </div>
          </div>
          
          <button
            @click="startRecordingForDrill"
            :disabled="isDrillRecording || isDrillEvaluating"
            class="drill-record-button"
          >
            <span v-if="isDrillRecording">⏹️ 停止</span>
            <span v-else-if="isDrillEvaluating">⏳ 評価中...</span>
            <span v-else>🎤 録音開始</span>
          </button>
          
          <div class="drill-result" v-if="drillResult">
            <p>スコア: {{ drillResult.score }}点</p>
            <p v-if="drillResult.success" class="success-message">✅ 成功！</p>
            <p v-else class="retry-message">もう一度挑戦してみましょう</p>
          </div>
        </div>
        
        <!-- C: リンク＆ブレイク -->
        <div v-if="currentDrill === 'link'" class="drill-section link-drill">
          <p class="drill-description">
            ターゲット語の前後の単語とのリンクを練習しましょう。
          </p>
          
          <div class="link-examples">
            <div class="link-example-item">
              <span class="example-label">リンク有:</span>
              <span class="example-text">{{ getLinkExample(true) }}</span>
            </div>
            <div class="link-example-item">
              <span class="example-label">リンク無:</span>
              <span class="example-text">{{ getLinkExample(false) }}</span>
            </div>
          </div>
          
          <div class="reference-audio" v-if="linkAudioUrl">
            <audio :src="linkAudioUrl" controls></audio>
          </div>
          
          <button
            @click="startRecordingForDrill"
            :disabled="isDrillRecording || isDrillEvaluating"
            class="drill-record-button"
          >
            <span v-if="isDrillRecording">⏹️ 停止</span>
            <span v-else-if="isDrillEvaluating">⏳ 評価中...</span>
            <span v-else>🎤 録音開始</span>
          </button>
          
          <div class="drill-result" v-if="drillResult">
            <p>スコア: {{ drillResult.score }}点</p>
            <p v-if="drillResult.success" class="success-message">✅ 成功！</p>
            <p v-else class="retry-message">もう一度挑戦してみましょう</p>
          </div>
        </div>
      </div>
      
      <!-- 進捗表示 -->
      <div class="drill-footer">
        <div class="progress-info">
          <span>試行回数: {{ totalTries }} / 3</span>
        </div>
        
        <div class="drill-navigation">
          <button
            v-if="currentDrillIndex > 0"
            @click="previousDrill"
            class="nav-button"
          >
            ← 前へ
          </button>
          
          <button
            v-if="!drillResult?.success && currentDrillIndex < drillSequence.length - 1"
            @click="nextDrill"
            class="nav-button"
          >
            次へ →
          </button>
          
          <button
            v-if="drillResult?.success"
            @click="handleSuccess"
            class="success-button"
          >
            ✅ 完了
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { AzureSpeechEvaluator } from '../utils/azureSpeechEvaluator'

const props = defineProps({
  targetWord: {
    type: Object,
    required: true
  },
  gateType: {
    type: String,
    required: true // 'flow_priority' or 'word_priority'
  },
  referenceText: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close', 'success'])

// 状態
const currentDrillIndex = ref(0)
const isDrillRecording = ref(false)
const isDrillEvaluating = ref(false)
const totalTries = ref(0)
const drillResult = ref(null)

// ドリルシーケンス（ゲートタイプに応じて）
const drillSequence = computed(() => {
  if (props.gateType === 'flow_priority') {
    return ['rhythm', 'link', 'minimal_pair'] // A → C → B
  } else {
    return ['minimal_pair', 'rhythm', 'link'] // B → A → C
  }
})

const currentDrill = computed(() => {
  return drillSequence.value[currentDrillIndex.value]
})

// インスタンス
let speechEvaluator = null
const referenceAudioRef = ref(null)

// 最小ペアデータ（簡易版）
const minimalPairs = ref([])

// 強勢ポイント
const stressPoints = ref([])

// 音声URL（将来実装: TTSまたは参照音声）
const referenceAudioUrl = ref(null)
const linkAudioUrl = ref(null)

onMounted(() => {
  initializeDrill()
})

onUnmounted(() => {
  cleanup()
})

// ドリル初期化
const initializeDrill = async () => {
  await initializeEvaluator()
  
  // Bドリル用の最小ペアを生成（問題音素から）
  if (props.targetWord.phonemes) {
    const problemPhonemes = props.targetWord.phonemes.filter(p => p.accuracy < 80)
    minimalPairs.value = generateMinimalPairs(problemPhonemes[0])
  }
  
  // Aドリル用の強勢ポイント抽出
  if (props.targetWord.syllables) {
    stressPoints.value = props.targetWord.syllables.map(s => ({
      syllable: s.grapheme || '',
      active: false
    }))
  }
}

const initializeEvaluator = async () => {
  if (!speechEvaluator) {
    speechEvaluator = new AzureSpeechEvaluator()
  }
}

const cleanup = () => {
  if (speechEvaluator) {
    speechEvaluator.cleanup()
  }
}

// ドリル録音開始
const startRecordingForDrill = async () => {
  if (isDrillRecording.value) {
    await stopRecordingForDrill()
    return
  }
  
  try {
    isDrillRecording.value = true
    totalTries.value++
    drillResult.value = null
    
    // リズムドリルの場合は参照音声再生
    if (currentDrill.value === 'rhythm' && referenceAudioRef.value) {
      referenceAudioRef.value.play()
    }
    
    // 簡易評価（実際はTTSされたターゲット語単独での評価）
    const drillText = getDrillText()
    const result = await speechEvaluator.evaluateWithSDK(drillText)
    
    isDrillRecording.value = false
    isDrillEvaluating.value = true
    
    // ターゲット語のスコアを取得
    const targetScore = result.words?.find(
      w => w.word === props.targetWord.word || w.word === props.targetWord.expectedWord
    )?.score || result.overall
    
    // 成功条件判定: word ≥ 80 または Δword ≥ +10
    const prevScore = props.targetWord.score || 0
    const delta = targetScore - prevScore
    const success = targetScore >= 80 || delta >= 10
    
    drillResult.value = {
      score: targetScore,
      delta,
      success
    }
    
    isDrillEvaluating.value = false
    
    // 成功したら自動で終了
    if (success) {
      setTimeout(() => {
        handleSuccess()
      }, 1500)
    }
    
    // 最大3トライで打ち切り
    if (totalTries.value >= 3 && !success) {
      setTimeout(() => {
        handleClose()
      }, 2000)
    }
    
  } catch (error) {
    console.error('ドリル評価エラー:', error)
    
    // エラーの種類に応じて処理を分岐
    const errorCode = error.code || ''
    const errorType = error.type || ''
    
    // 無音タイムアウトの場合はメッセージを表示しない
    if (errorCode === 'SILENCE_TIMEOUT' || errorType === 'silence') {
      console.log('🔇 無音のため評価を終了（メッセージ表示なし）')
      // メッセージは表示せず、状態をリセットするだけ
      isDrillRecording.value = false
      isDrillEvaluating.value = false
      return
    }
    
    // マイク権限エラーの場合はメッセージを表示
    if (errorCode === 'MICROPHONE_PERMISSION_DENIED' || errorType === 'permission') {
      alert(`エラー: ${error.message}\n\nブラウザの設定からマイクの許可をお願いします。`)
    } else {
      // その他のエラー（ネットワークエラーなど）はメッセージを表示
      alert(`エラー: ${error.message}`)
    }
    
    isDrillRecording.value = false
    isDrillEvaluating.value = false
  }
}

// ドリル録音停止
const stopRecordingForDrill = async () => {
  isDrillRecording.value = false
  
  if (speechEvaluator) {
    await speechEvaluator.stopRealtimeEvaluation()
  }
}

// ドリルテキスト取得
const getDrillText = () => {
  if (currentDrill.value === 'minimal_pair') {
    // 最小ペアの単語を結合
    return minimalPairs.value.map(p => p.word).join(' ')
  } else if (currentDrill.value === 'link') {
    // 前後の単語を含む
    return props.referenceText
  } else {
    // リズムドリルはターゲット語のみ
    return props.targetWord.word || props.targetWord.expectedWord
  }
}

// 最小ペア生成（簡易版）
const generateMinimalPairs = (phoneme) => {
  if (!phoneme) return []
  
  // 音素別の最小ペア辞書（簡易版）
  const pairsMap = {
    'h': [{ word: 'how', phoneme: 'h' }, { word: 'cow', phoneme: 'k' }],
    'θ': [{ word: 'think', phoneme: 'θ' }, { word: 'sink', phoneme: 's' }],
    'r': [{ word: 'right', phoneme: 'r' }, { word: 'light', phoneme: 'l' }],
    'l': [{ word: 'light', phoneme: 'l' }, { word: 'right', phoneme: 'r' }]
  }
  
  return pairsMap[phoneme.phoneme] || [
    { word: props.targetWord.word, phoneme: phoneme.phoneme }
  ]
}

// リンク例取得
const getLinkExample = (withLink) => {
  // 簡易版: 前後の単語を含む
  const words = props.referenceText.toLowerCase().split(' ')
  const targetIndex = words.findIndex(w => 
    w === props.targetWord.word?.toLowerCase() ||
    w === props.targetWord.expectedWord?.toLowerCase()
  )
  
  if (targetIndex === -1) return props.referenceText
  
  const prevWord = targetIndex > 0 ? words[targetIndex - 1] : ''
  const targetWord = words[targetIndex]
  const nextWord = targetIndex < words.length - 1 ? words[targetIndex + 1] : ''
  
  if (withLink) {
    return `${prevWord}‿${targetWord}‿${nextWord}`.trim()
  } else {
    return `${prevWord} ${targetWord} ${nextWord}`.trim()
  }
}

// 参照音声再生終了
const onReferenceAudioEnded = () => {
  // 自動再生終了時の処理
}

// 前のドリル
const previousDrill = () => {
  if (currentDrillIndex.value > 0) {
    currentDrillIndex.value--
    drillResult.value = null
    totalTries.value = 0
  }
}

// 次のドリル
const nextDrill = () => {
  if (currentDrillIndex.value < drillSequence.value.length - 1) {
    currentDrillIndex.value++
    drillResult.value = null
    totalTries.value = 0
  }
}

// 成功処理
const handleSuccess = () => {
  emit('success', {
    drillType: currentDrill.value,
    result: drillResult.value,
    tries: totalTries.value
  })
}

// 閉じる
const handleClose = () => {
  emit('close')
}
</script>

<style scoped>
.quick-drill-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.drill-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.drill-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.drill-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1f2937;
}

.close-button {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-button:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.drill-body {
  padding: 1.5rem;
}

.drill-description {
  margin-bottom: 1.5rem;
  color: #6b7280;
  line-height: 1.6;
}

.reference-audio {
  margin: 1rem 0;
}

.stress-indicator {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin: 1.5rem 0;
  flex-wrap: wrap;
}

.stress-point {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-radius: 6px;
  font-weight: bold;
  transition: all 0.3s;
}

.stress-point.active {
  background: #10b981;
  color: white;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.minimal-pairs-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1.5rem 0;
}

.pair-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.pair-word {
  font-size: 1.25rem;
  font-weight: bold;
  color: #1f2937;
}

.pair-phoneme {
  font-size: 1rem;
  color: #6b7280;
  font-family: 'Times New Roman', serif;
}

.link-examples {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1.5rem 0;
}

.link-example-item {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.example-label {
  font-weight: bold;
  color: #6b7280;
  margin-right: 0.5rem;
}

.example-text {
  font-size: 1.1rem;
  color: #1f2937;
}

.drill-record-button {
  width: 100%;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;
}

.drill-record-button:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.drill-record-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.drill-result {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  text-align: center;
}

.success-message {
  color: #10b981;
  font-weight: bold;
  margin-top: 0.5rem;
}

.retry-message {
  color: #f59e0b;
  margin-top: 0.5rem;
}

.drill-footer {
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.progress-info {
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
}

.drill-navigation {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.nav-button,
.success-button {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.nav-button {
  background: #e5e7eb;
  color: #1f2937;
}

.nav-button:hover {
  background: #d1d5db;
}

.success-button {
  background: #10b981;
  color: white;
}

.success-button:hover {
  background: #059669;
}

@media (max-width: 768px) {
  .drill-content {
    max-width: 100%;
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
  }
  
  .stress-indicator {
    gap: 0.25rem;
  }
  
  .stress-point {
    padding: 0.4rem 0.75rem;
    font-size: 0.9rem;
  }
}
</style>

